import React, { useState, useMemo, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { placeTrade, fetchMyPositions, fetchWallet, phoneStatus } from "./client";
import { useI18n } from "./i18n";
import { dispatchWalletChange, dispatchMarketChange, onWalletChange } from "./walletEvents";
import * as lmsr from "./lmsr";
import PhoneVerificationModal from "./PhoneVerificationModal";

/* ═══════════════════════════════════════════════════════════════════════════
   I18N
═══════════════════════════════════════════════════════════════════════════ */
const WT = {
  "title":           { fr:"Trader",                    en:"Trade",                  es:"Operar",                  de:"Traden",                pt:"Operar",               ar:"تداول" },
  "side.buy":        { fr:"Acheter",                   en:"Buy",                    es:"Comprar",                 de:"Kaufen",                pt:"Comprar",              ar:"شراء" },
  "side.sell":       { fr:"Vendre",                    en:"Sell",                   es:"Vender",                  de:"Verkaufen",             pt:"Vender",               ar:"بيع" },
  "lbl.outcome":     { fr:"Résultat",                  en:"Outcome",                es:"Resultado",               de:"Ergebnis",              pt:"Resultado",            ar:"النتيجة" },
  "lbl.sellpos":     { fr:"Vos positions à vendre",     en:"Your positions to sell", es:"Tus posiciones a vender", de:"Deine verkäuflichen Positionen", pt:"Suas posições para vender", ar:"مراكزك القابلة للبيع" },
  "empty.sellpos":   { fr:"Vous n'avez aucune position ouverte à vendre sur ce marché.",
                       en:"You have no open position to sell on this market.",
                       es:"No tienes ninguna posición abierta para vender en este mercado.",
                       de:"Du hast keine offene Position, die du auf diesem Markt verkaufen kannst.",
                       pt:"Você não tem nenhuma posição aberta para vender neste mercado.",
                       ar:"ليس لديك أي مركز مفتوح للبيع في هذا السوق." },
  "lbl.shares":      { fr:"Nombre de parts",           en:"Number of shares",       es:"Número de partes",        de:"Anzahl Anteile",        pt:"Número de shares",     ar:"عدد الأسهم" },
  "lbl.slippage":    { fr:"Tolérance de slippage",     en:"Slippage tolerance",     es:"Tolerancia al deslizamiento", de:"Slippage-Toleranz", pt:"Tolerância de slippage", ar:"تحمّل الانزلاق" },
  "preview.current": { fr:"Prix actuel",               en:"Current price",          es:"Precio actual",           de:"Aktueller Preis",       pt:"Preço atual",          ar:"السعر الحالي" },
  "preview.effective":{fr:"Prix effectif",             en:"Effective price",        es:"Precio efectivo",         de:"Effektiver Preis",      pt:"Preço efetivo",        ar:"السعر الفعلي" },
  "preview.slippage":{ fr:"Slippage",                  en:"Slippage",               es:"Deslizamiento",           de:"Slippage",              pt:"Slippage",             ar:"الانزلاق" },
  "preview.cost":    { fr:"Coût total",                en:"Total cost",             es:"Coste total",             de:"Gesamtkosten",          pt:"Custo total",          ar:"التكلفة الإجمالية" },
  "preview.receive": { fr:"Vous recevez",              en:"You receive",            es:"Recibes",                 de:"Sie erhalten",          pt:"Você recebe",          ar:"تستلم" },
  "preview.balance": { fr:"Solde après",               en:"Balance after",          es:"Saldo después",           de:"Guthaben danach",       pt:"Saldo depois",         ar:"الرصيد بعد" },
  "btn.confirm":     { fr:"Confirmer le trade",        en:"Confirm trade",          es:"Confirmar operación",     de:"Trade bestätigen",      pt:"Confirmar operação",   ar:"تأكيد الصفقة" },
  "btn.confirming":  { fr:"Confirmation…",             en:"Confirming…",            es:"Confirmando…",            de:"Bestätige…",            pt:"Confirmando…",         ar:"جاري التأكيد…" },
  "btn.close":       { fr:"Fermer",                    en:"Close",                  es:"Cerrar",                  de:"Schließen",             pt:"Fechar",               ar:"إغلاق" },
  "hint.insufficient":{ fr:"Solde insuffisant",        en:"Insufficient balance",   es:"Saldo insuficiente",      de:"Unzureichendes Guthaben", pt:"Saldo insuficiente", ar:"رصيد غير كافٍ" },
  "hint.noshares":   { fr:"Pas assez de parts à vendre",en:"Not enough shares to sell", es:"No hay suficientes partes para vender", de:"Nicht genug Anteile zum Verkauf", pt:"Não há shares suficientes para vender", ar:"لا توجد أسهم كافية للبيع" },
  "hint.lmsr":       { fr:"Le prix bouge à chaque trade selon le carnet LMSR.",
                       en:"The price moves with each trade per the LMSR order flow.",
                       es:"El precio se mueve con cada operación según el flujo LMSR.",
                       de:"Der Preis bewegt sich mit jedem Trade gemäß LMSR-Flow.",
                       pt:"O preço se move a cada operação conforme o fluxo LMSR.",
                       ar:"يتحرّك السعر مع كل صفقة وفقًا لتدفق LMSR." },
  "success":         { fr:"Trade exécuté !",           en:"Trade executed!",        es:"¡Operación ejecutada!",   de:"Trade ausgeführt!",     pt:"Operação executada!",  ar:"تم تنفيذ الصفقة!" },
  "shares":          { fr:"parts",                     en:"shares",                 es:"partes",                  de:"Anteile",               pt:"shares",               ar:"أسهم" },
};

function wt(key, lang) {
  return WT[key]?.[lang] ?? WT[key]?.fr ?? key;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Animations & styled-components
═══════════════════════════════════════════════════════════════════════════ */
const fadeIn  = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const checkIn = keyframes`
  0%   { transform: scale(0) rotate(-45deg); opacity: 0 }
  60%  { transform: scale(1.2) rotate(5deg);  opacity: 1 }
  100% { transform: scale(1) rotate(0deg);    opacity: 1 }
`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 300;
  background: rgba(0,0,0,.82);
  backdrop-filter: blur(10px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem; animation: ${fadeIn} .15s ease;
`;
const Modal = styled.div`
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.borderLight};
  border-radius: 18px; width: 100%; max-width: 460px;
  max-height: 92vh; overflow-y: auto;
  animation: ${slideUp} .2s ease;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const Head = styled.div`
  padding: 1.1rem 1.5rem; border-bottom: 1px solid ${({theme}) => theme.border};
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
  position: sticky; top: 0; background: ${({theme}) => theme.bgCard}; z-index: 2;
`;
const HeadText = styled.div`flex: 1; min-width: 0;`;
const Title = styled.h2`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.05rem; font-weight: 700; letter-spacing: -.02em;
  line-height: 1.25;
`;
const MarketTitle = styled.p`
  font-size: .72rem; color: ${({theme}) => theme.textMuted};
  margin-top: .2rem; line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
`;
const Close = styled.button`
  background: none; border: none; color: ${({theme}) => theme.textMuted};
  cursor: pointer; font-size: 1.1rem; padding: 0;
  &:hover { color: ${({theme}) => theme.text}; }
`;
const Body = styled.div`
  padding: 1.1rem 1.5rem; display: flex; flex-direction: column; gap: 1rem;
`;

/* Side tabs */
const SideTabs = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: .35rem;
  background: ${({theme}) => theme.bg};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 10px; padding: .3rem;
`;
const SideTab = styled.button`
  padding: .6rem; border-radius: 7px; border: none;
  background: ${({$active, $buy, theme}) =>
    !$active ? "transparent" : $buy ? "rgba(0,230,118,.12)" : "rgba(255,59,92,.12)"};
  border: 1px solid ${({$active, $buy, theme}) =>
    !$active ? "transparent" : $buy ? theme.green : theme.red};
  color: ${({$active, $buy, theme}) =>
    !$active ? theme.textMuted : $buy ? theme.green : theme.red};
  font-family: ${({theme}) => theme.fontBody};
  font-size: .82rem; font-weight: 600; letter-spacing: .02em;
  cursor: pointer; transition: all .15s;
`;

/* Outcomes */
const OutcomeList = styled.div`display: flex; flex-direction: column; gap: .4rem;`;
const OutcomeBtn = styled.button`
  display: flex; justify-content: space-between; align-items: center;
  padding: .7rem .9rem;
  background: ${({$active}) => $active ? "rgba(79,195,247,.08)" : "transparent"};
  border: 1px solid ${({$active, theme}) => $active ? theme.accent + "88" : theme.border};
  border-radius: 9px; cursor: pointer; transition: all .15s;
  &:hover { background: ${({theme}) => theme.bgHover}; border-color: ${({theme}) => theme.borderLight}; }
`;
const OutcomeName = styled.span`
  font-family: ${({theme}) => theme.fontBody};
  font-size: .85rem; font-weight: 600;
  color: ${({$active, theme}) => $active ? theme.text : theme.textDim};
`;
const OutcomePrice = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .92rem; font-weight: 700;
  color: ${({$active, theme}) => $active ? theme.accent : theme.text};
`;

/* Positions à vendre (mode SELL) */
const SellPosList = styled.div`display: flex; flex-direction: column; gap: .4rem;`;
const SellPosBtn = styled.button`
  display: flex; justify-content: space-between; align-items: center; gap: .6rem;
  padding: .7rem .9rem; width: 100%; text-align: left;
  background: ${({$active, theme}) => $active ? "rgba(255,59,92,.08)" : "transparent"};
  border: 1px solid ${({$active, theme}) => $active ? theme.red + "88" : theme.border};
  border-radius: 9px; cursor: pointer; transition: all .15s;
  &:hover { background: ${({theme}) => theme.bgHover}; border-color: ${({theme}) => theme.borderLight}; }
`;
const SellPosLeft  = styled.div`display: flex; flex-direction: column; gap: .15rem; min-width: 0;`;
const SellPosName  = styled.span`
  font-family: ${({theme}) => theme.fontBody}; font-size: .85rem; font-weight: 600;
  color: ${({$active, theme}) => $active ? theme.text : theme.textDim};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const SellPosSub   = styled.span`font-family: ${({theme}) => theme.fontMono}; font-size: .64rem; color: ${({theme}) => theme.textMuted};`;
const SellPosRight = styled.div`display: flex; flex-direction: column; align-items: flex-end; gap: .15rem; flex-shrink: 0;`;
const SellPosShares= styled.span`
  font-family: ${({theme}) => theme.fontMono}; font-size: .85rem; font-weight: 700;
  color: ${({$active, theme}) => $active ? theme.red : theme.text};
`;
const SellPosVal   = styled.span`font-family: ${({theme}) => theme.fontMono}; font-size: .62rem; color: ${({theme}) => theme.textMuted};`;
const EmptySellPos = styled.div`
  padding: 1rem; text-align: center; font-size: .74rem; color: ${({theme}) => theme.textMuted};
  border: 1px dashed ${({theme}) => theme.border}; border-radius: 9px;
`;

/* Shares input */
const Field = styled.div`display: flex; flex-direction: column; gap: .4rem;`;
const Label = styled.label`
  font-size: .67rem; text-transform: uppercase; letter-spacing: .08em;
  color: ${({theme}) => theme.textMuted};
`;
const InputWrap = styled.div`
  display: flex; align-items: center;
  border: 1px solid ${({theme, danger}) => danger ? theme.red + "88" : theme.border};
  border-radius: 8px; overflow: hidden;
  &:focus-within { border-color: ${({theme, danger}) => danger ? theme.red : theme.borderLight}; }
`;
const Input = styled.input`
  flex: 1; background: transparent; border: none; outline: none;
  color: ${({theme}) => theme.text};
  font-family: ${({theme}) => theme.fontMono};
  font-size: .92rem; padding: .65rem .85rem; width: 100%;
  &::placeholder { color: ${({theme}) => theme.textMuted}; }
`;
const StepBtn = styled.button`
  padding: .65rem .8rem; background: ${({theme}) => theme.bgHover};
  border: none; border-left: 1px solid ${({theme}) => theme.border};
  color: ${({theme}) => theme.text};
  font-family: ${({theme}) => theme.fontMono}; font-size: .9rem;
  cursor: pointer;
  &:hover { background: ${({theme}) => theme.borderLight}; }
`;
const QuickRow = styled.div`display: flex; gap: .35rem; flex-wrap: wrap;`;
const QuickBtn = styled.button`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .68rem; padding: .25rem .65rem; border-radius: 4px;
  border: 1px solid ${({theme}) => theme.border};
  background: transparent; color: ${({theme}) => theme.textDim};
  cursor: pointer; transition: all .15s;
  &:hover { border-color: ${({theme}) => theme.borderLight}; color: ${({theme}) => theme.text}; }
`;

/* Slippage selector */
const SlipRow = styled.div`display: flex; gap: .35rem;`;
const SlipChip = styled.button`
  flex: 1; padding: .4rem .6rem; border-radius: 6px;
  font-family: ${({theme}) => theme.fontMono};
  font-size: .7rem; letter-spacing: .03em;
  background: ${({$active}) => $active ? "rgba(79,195,247,.08)" : "transparent"};
  border: 1px solid ${({$active, theme}) => $active ? theme.accent + "88" : theme.border};
  color: ${({$active, theme}) => $active ? theme.accent : theme.textDim};
  cursor: pointer; transition: all .15s;
`;

/* Preview summary */
const Summary = styled.div`
  background: ${({theme}) => theme.bg};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 9px; padding: .85rem 1rem;
  display: flex; flex-direction: column; gap: .4rem;
`;
const SumRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  font-size: .78rem;
`;
const SumKey = styled.span`color: ${({theme}) => theme.textMuted};`;
const SumVal = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  color: ${({theme, $accent, $green, $red}) =>
    $green  ? theme.green :
    $red    ? theme.red :
    $accent ? theme.accent :
              theme.text};
  font-weight: ${({$accent, $green, $red}) => ($accent || $green || $red) ? 600 : 400};
`;
const Divider = styled.div`height: 1px; background: ${({theme}) => theme.border}; margin: .1rem 0;`;

/* Slippage badge (colored) */
const SlipBadge = styled.span`
  display: inline-flex; align-items: center; gap: .25rem;
  font-family: ${({theme}) => theme.fontMono};
  font-size: .68rem; font-weight: 600;
  padding: .12rem .5rem; border-radius: 100px;
  background: ${({$level, theme}) =>
    $level === "low"  ? `${theme.green}1a` :
    $level === "med"  ? `${theme.gold}1a` :
                        `${theme.red}1a`};
  color: ${({$level, theme}) =>
    $level === "low"  ? theme.green :
    $level === "med"  ? theme.gold :
                        theme.red};
  border: 1px solid ${({$level, theme}) =>
    $level === "low"  ? `${theme.green}55` :
    $level === "med"  ? `${theme.gold}55` :
                        `${theme.red}55`};
`;

const Hint = styled.div`
  display: flex; align-items: flex-start; gap: .4rem;
  font-size: .68rem; color: ${({theme}) => theme.textMuted};
  line-height: 1.45;
  padding: .55rem .75rem;
  background: rgba(79,195,247,.05);
  border: 1px solid ${({theme}) => theme.accent}22;
  border-radius: 7px;
`;

const ErrorMsg = styled.div`
  font-size: .75rem; color: ${({theme}) => theme.red}; text-align: center;
`;

const Submit = styled.button`
  width: 100%; padding: .9rem;
  border-radius: 9px; border: none;
  background: ${({theme, $buy, disabled}) =>
    disabled ? theme.bgHover :
    $buy     ? theme.green :
                theme.red};
  color: ${({theme, disabled}) => disabled ? theme.textMuted : "#000"};
  font-family: ${({theme}) => theme.fontBody};
  font-size: .9rem; font-weight: 700; letter-spacing: .03em;
  cursor: ${({disabled}) => disabled ? "not-allowed" : "pointer"};
  transition: opacity .15s;
  &:hover:not(:disabled) { opacity: .9; }
`;

/* Success */
const Success = styled.div`
  padding: 2.5rem 1.5rem; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 1rem;
`;
const CheckCircle = styled.div`
  width: 64px; height: 64px; border-radius: 50%;
  background: rgba(0,230,118,.12);
  border: 2px solid ${({theme}) => theme.green};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.8rem;
  animation: ${checkIn} .4s cubic-bezier(.4,0,.2,1) both;
`;
const SuccessTitle = styled.h3`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.15rem; font-weight: 700;
  color: ${({theme}) => theme.text};
`;
const SuccessSub = styled.p`
  font-size: .8rem; color: ${({theme}) => theme.textMuted};
  max-width: 320px; line-height: 1.5;
`;
const DoneBtn = styled.button`
  padding: .55rem 1.5rem; border-radius: 8px;
  border: 1px solid ${({theme}) => theme.borderLight};
  background: transparent; color: ${({theme}) => theme.text};
  font-family: ${({theme}) => theme.fontBody};
  font-size: .78rem; font-weight: 600; cursor: pointer;
  &:hover { background: ${({theme}) => theme.bgHover}; }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function TradeModal({ market, onClose, initialSide, initialOutcomeId }) {
  const { lang } = useI18n();
  const t = (key) => wt(key, lang);

  /* ── State ──────────────────────────────────────────────────────────── */
  const [side,     setSide]     = useState(initialSide === "SELL" ? "SELL" : "BUY");
  const [outcomeId,setOutcomeId]= useState(initialOutcomeId ?? market?.outcomes?.[0]?.id ?? null);
  const [shares,   setShares]   = useState("10");
  const [slip,     setSlip]     = useState(1.0);  // %  (0.5 / 1 / 5)
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(false);
  const [myShares, setMyShares] = useState({});   // {outcome_id: shares_owned}
  const [balance,  setBalance]  = useState(0);    // ← fetch en interne
const [needsPhone,   setNeedsPhone]   = useState(false);
const [pendingTrade, setPendingTrade] = useState(null);
  /* ── Fetch wallet balance + écouter les changements ─────────────────── */
  useEffect(() => {
    const loadBalance = async () => {
      try {
        const r = await fetchWallet();
        setBalance(parseFloat(r.data?.usdc_balance || 0));
      } catch {}
    };
    loadBalance();
    // Re-fetch quand un autre composant signale un changement de wallet
    const off = onWalletChange(() => loadBalance());
    return off;
  }, []);

  /* ── Charger les parts détenues (pour la vente) ─────────────────────── */
  // ⚠ Matching robuste : selon le serializer, `p.outcome.market` peut être
  //   un ID (int), un titre (string), un objet imbriqué, ou absent.
  //   On accepte tous les formats + fallback : si market est absent, on prend
  //   les positions dont l'outcome.id correspond à un outcome de ce marché.
  useEffect(() => {
    if (!market?.id) return;
    let alive = true;

    // IDs des outcomes de CE marché (fallback si pas de market sur la position)
    const marketOutcomeIds = new Set((market.outcomes || []).map(o => o.id));

    fetchMyPositions().then(r => {
      if (!alive) return;
      const map = {};
      const positions = r.data?.results || r.data || [];

      positions.forEach(p => {
        // Trouver l'outcome_id, quel que soit le format renvoyé
        const oid = p.outcome?.id ?? p.outcome_id ?? p.outcome;
        if (!oid) return;

        // Trouver le market_id de la position, tolérant aux formats :
        //   p.outcome.market (int), p.outcome.market.id, p.market, p.market_id
        const positionMarketId =
          p.outcome?.market?.id ??
          p.outcome?.market ??
          p.outcome?.market_id ??
          p.market?.id ??
          p.market ??
          p.market_id ??
          null;

        const sharesNum = parseFloat(p.shares || 0);
        if (sharesNum <= 0) return;

        // Match strict si market_id présent, sinon fallback sur outcome_id appartenant à ce marché
        const matches =
          positionMarketId !== null
            ? String(positionMarketId) === String(market.id)
            : marketOutcomeIds.has(oid);

        if (matches) {
          map[oid] = {
            shares:    sharesNum,
            avgPrice:  parseFloat(p.avg_price ?? p.avgPrice ?? 0) || 0,
            costBasis: parseFloat(p.cost_basis ?? p.costBasis ?? 0) || 0,
          };
        }
      });

      setMyShares(map);
    }).catch(() => {});

    return () => { alive = false; };
  }, [market?.id, market?.outcomes]);

  /* ── Données LMSR du marché ────────────────────────────────────────── */
  const outcomes = market?.outcomes || [];
  const b        = parseFloat(market?.liquidity_param) || 1442.7;
  const qs       = outcomes.map(o => parseFloat(o.shares_outstanding) || 0);

  /* ── Calculs LMSR live ──────────────────────────────────────────────── */
  const sharesNum = parseFloat(shares) || 0;
  const outcomeIdx = outcomes.findIndex(o => o.id === outcomeId);

  const calc = useMemo(() => {
    if (outcomeIdx < 0 || sharesNum <= 0) {
      return { currentPrice: 0, effectivePrice: 0, cost: 0, slippage: 0, level: "low" };
    }
    const delta = side === "BUY" ? sharesNum : -sharesNum;
    const currentPrice    = lmsr.priceOne(qs, b, outcomeIdx);
    const rawCost         = lmsr.costToTrade(qs, b, outcomeIdx, delta);
    const cost            = Math.abs(rawCost);
    const effectivePrice  = cost / sharesNum;
    const slippage        = lmsr.slippagePct(qs, b, outcomeIdx, delta);
    const level =
      slippage < 1  ? "low"  :
      slippage < 5  ? "med"  :
                      "high";
    return { currentPrice, effectivePrice, cost, slippage, level };
  }, [outcomeIdx, sharesNum, side, qs.join(","), b]);

  /* ── Validations ────────────────────────────────────────────────────── */
  const ownedShares = myShares[outcomeId]?.shares || 0;
  const insufficientBalance = side === "BUY"  && calc.cost > balance;
  const insufficientShares  = side === "SELL" && sharesNum > ownedShares;
  const canTrade = sharesNum > 0 && !insufficientBalance && !insufficientShares && !loading;

  /* ── Pré-remplissage : arrivée directe depuis "Mes positions" ────────
     Dès que les positions du marché sont chargées, on renseigne
     automatiquement le nombre de parts détenues sur l'outcome ciblé,
     une seule fois. */
  const [sellAutoFilled, setSellAutoFilled] = useState(false);
  useEffect(() => {
    if (
      !sellAutoFilled &&
      initialSide === "SELL" &&
      initialOutcomeId != null &&
      myShares[initialOutcomeId]
    ) {
      setShares(String(Math.floor(myShares[initialOutcomeId].shares)));
      setSellAutoFilled(true);
    }
  }, [myShares, initialSide, initialOutcomeId, sellAutoFilled]);

const [phoneStatusKnown, setPhoneStatusKnown] = useState(false);

useEffect(() => {
  let cancelled = false;
  phoneStatus()
    .then(r => {
      if (cancelled) return;
      if (!r.data?.phone_verified) setNeedsPhone(true);
      setPhoneStatusKnown(true);
    })
    .catch(() => {
      if (!cancelled) setPhoneStatusKnown(true);   // on laisse passer en cas d'erreur
    });
  return () => { cancelled = true; };
}, []);
/* ── Check phone verification ─────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;

    const checkPhone = async () => {
      try {
        const r = await phoneStatus();
        if (cancelled) return;
        if (!r.data?.phone_verified) {
          setNeedsPhone(true);
        }
      } catch (err) {
        console.warn("Phone status check failed:", err);
      }
    };

    checkPhone();
    return () => { cancelled = true; };
  }, []);

  /* ── Submit ─────────────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!canTrade) return;
    setLoading(true); setError(null);

    try {
      // max_cost = coût LMSR * (1 + slip%)  → protection slippage backend
      const maxCost = side === "BUY"
        ? +(calc.cost * (1 + slip / 100)).toFixed(2)
        : undefined;

      await placeTrade(market.id, {
        outcome_id: outcomeId,
        side,
        shares:     sharesNum,
        max_cost:   maxCost,
      });

      // Notifier le reste de l'app
      dispatchWalletChange();
      dispatchMarketChange({ marketId: market.id });

      setSuccess(true);
      setTimeout(onClose, 10800);
    } catch (e) {
        if (e.response?.status === 403 &&
      e.response.data?.detail?.includes("téléphone")) {
  //  toast.err("Vérifiez votre téléphone d'abord.");
    onClose();
    // Trigger phone verification modal au niveau parent
    return;
  }
      const msg = e?.response?.data?.detail
               || e?.response?.data
               || "Erreur lors du trade.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };
  if (!phoneStatusKnown) {
  return null;   // ou un spinner si tu veux
}
// Juste avant le `return (` principal du composant
if (false && needsPhone) {
  return (
    <PhoneVerificationModal
      open={true}
      onClose={() => { setNeedsPhone(false); onClose(); }}
      onVerified={() => {
        setNeedsPhone(false);
        // L'utilisateur reste dans TradeModal et peut maintenant trader
      }}
    />
  );
}
  /* ─── Success screen ─── */
  if (success) {
    return (
      <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
        <Modal>
          <Success>
            <CheckCircle>✓</CheckCircle>
            <SuccessTitle>{t("success")}</SuccessTitle>
            <SuccessSub>
              {sharesNum} {t("shares")} · {side === "BUY" ? "−" : "+"}
              {calc.cost.toFixed(5)} EVP
            </SuccessSub>
            <DoneBtn onClick={onClose}>{t("btn.close")}</DoneBtn>
          </Success>
        </Modal>
      </Overlay>
    );
  }

  /* ─── Main render ─── */
  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <Modal>
        <Head>
          <HeadText>
            <Title>{t("title")}</Title>
            <MarketTitle>{market?.title}</MarketTitle>
          </HeadText>
          <Close onClick={onClose}>✕</Close>
        </Head>

        <Body>
          {/* BUY / SELL */}
          <SideTabs>
            <SideTab $active={side==="BUY"}  $buy onClick={() => setSide("BUY")}>
              ↑ {t("side.buy")}
            </SideTab>
            <SideTab $active={side==="SELL"}      onClick={() => setSide("SELL")}>
              ↓ {t("side.sell")}
            </SideTab>
          </SideTabs>

          {/* Outcome selection (BUY) / Positions à vendre (SELL) */}
          <Field>
            <Label>{side === "SELL" ? t("lbl.sellpos") : t("lbl.outcome")}</Label>
            {side === "SELL" ? (
              (() => {
                const sellable = outcomes.filter(o => (myShares[o.id]?.shares || 0) > 0);
                if (sellable.length === 0) {
                  return <EmptySellPos>{t("empty.sellpos")}</EmptySellPos>;
                }
                return (
                  <SellPosList>
                    {sellable.map(o => {
                      const idx = outcomes.findIndex(x => x.id === o.id);
                      const price = lmsr.priceOne(qs, b, idx);
                      const owned = myShares[o.id]?.shares || 0;
                      const active = outcomeId === o.id;
                      return (
                        <SellPosBtn key={o.id} $active={active}
                          onClick={() => { setOutcomeId(o.id); setShares(String(Math.floor(owned))); }}>
                          <SellPosLeft>
                            <SellPosName $active={active}>{o.name}</SellPosName>
                            <SellPosSub>{(price * 100).toFixed(1)}%</SellPosSub>
                          </SellPosLeft>
                          <SellPosRight>
                            <SellPosShares $active={active}>{owned.toFixed(0)} {t("shares")}</SellPosShares>
                            <SellPosVal>≈ {(owned * price).toFixed(2)} EVP</SellPosVal>
                          </SellPosRight>
                        </SellPosBtn>
                      );
                    })}
                  </SellPosList>
                );
              })()
            ) : (
              <OutcomeList>
                {outcomes.map((o, i) => {
                  const price = lmsr.priceOne(qs, b, i);
                  return (
                    <OutcomeBtn key={o.id} $active={outcomeId === o.id}
                      onClick={() => setOutcomeId(o.id)}>
                      <OutcomeName $active={outcomeId === o.id}>{o.name}</OutcomeName>
                      <OutcomePrice $active={outcomeId === o.id}>
                        {(price * 100).toFixed(1)}%
                      </OutcomePrice>
                    </OutcomeBtn>
                  );
                })}
              </OutcomeList>
            )}
          </Field>

          {/* Shares */}
          <Field>
            <Label>
              {t("lbl.shares")}
              {side === "SELL" && (
                <span style={{marginLeft:".5rem", color:"#5a6a7a", fontFamily:"DM Mono,monospace",
                  fontSize:".62rem", letterSpacing:0, textTransform:"none"}}>
                  · vous avez {ownedShares.toFixed(0)} parts
                </span>
              )}
            </Label>
            <InputWrap danger={insufficientBalance || insufficientShares ? 1 : 0}>
              <StepBtn onClick={() => setShares(String(Math.max(0, sharesNum - 1)))}>−</StepBtn>
              <Input type="number" min="0" step="1" value={shares}
                onChange={e => setShares(e.target.value)} />
              <StepBtn onClick={() => setShares(String(sharesNum + 1))}>+</StepBtn>
            </InputWrap>
            <QuickRow>
              {[1, 5, 10, 50, 100].map(n => (
                <QuickBtn key={n} onClick={() => setShares(String(n))}>{n}</QuickBtn>
              ))}
              {/* Bouton "MAX" en mode SELL — pratique pour vendre tout */}
              {side === "SELL" && ownedShares > 0 && (
                <QuickBtn onClick={() => setShares(String(Math.floor(ownedShares)))}>
                  MAX ({Math.floor(ownedShares)})
                </QuickBtn>
              )}
            </QuickRow>
          </Field>

          {/* Slippage tolerance (uniquement pour BUY) */}
          {side === "BUY" && (
            <Field>
              <Label>{t("lbl.slippage")}</Label>
              <SlipRow>
                {[0.5, 1, 5].map(v => (
                  <SlipChip key={v} $active={slip === v} onClick={() => setSlip(v)}>
                    {v}%
                  </SlipChip>
                ))}
              </SlipRow>
            </Field>
          )}

          {/* Preview LMSR */}
          {sharesNum > 0 && outcomeIdx >= 0 && (
            <Summary>
              <SumRow>
                <SumKey>{t("preview.current")}</SumKey>
                <SumVal>{(calc.currentPrice * 100).toFixed(2)}%</SumVal>
              </SumRow>
              <SumRow>
                <SumKey>
                  {t("preview.effective")}
                  {calc.slippage > 0.05 && (
                    <SlipBadge $level={calc.level} style={{marginLeft:".4rem"}}>
                      {t("preview.slippage")} {calc.slippage.toFixed(2)}%
                    </SlipBadge>
                  )}
                </SumKey>
                <SumVal>{(calc.effectivePrice * 100).toFixed(2)}%</SumVal>
              </SumRow>
              <Divider />
              <SumRow>
                <SumKey>{side === "BUY" ? t("preview.cost") : t("preview.receive")}</SumKey>
                <SumVal $accent>
                  {side === "BUY" ? "−" : "+"} {calc.cost.toFixed(2)} EVP
                </SumVal>
              </SumRow>
              <SumRow>
                <SumKey>{t("preview.balance")}</SumKey>
                <SumVal $green={side==="SELL"} $red={insufficientBalance}>
                  {(side === "BUY"
                      ? Math.max(0, balance - calc.cost)
                      : balance + calc.cost
                    ).toFixed(2)} EVP
                </SumVal>
              </SumRow>
            </Summary>
          )}

          {/* Hints */}
          {insufficientBalance && <ErrorMsg>{t("hint.insufficient")}</ErrorMsg>}
          {insufficientShares  && <ErrorMsg>{t("hint.noshares")}</ErrorMsg>}
          {error              && <ErrorMsg>⚠ {error}</ErrorMsg>}

          <Hint>
            <span>💡</span>
            <span>{t("hint.lmsr")}</span>
          </Hint>

          {/* Submit */}
          <Submit $buy={side === "BUY"} disabled={!canTrade} onClick={handleSubmit}>
            {loading ? t("btn.confirming") : (
              <>
                {side === "BUY" ? t("side.buy") : t("side.sell")}
                {" "}{sharesNum > 0 && `${sharesNum} ${t("shares")} `}
                · {calc.cost.toFixed(2)} EVP
              </>
            )}
          </Submit>
        </Body>
      </Modal>
    </Overlay>
  );
}
